import { useSyncExternalStore } from "react";
import { postEvent, supports } from "@telegram-apps/sdk";
import { ensureSdkPolyfills } from "./launch";

/**
 * ── PORTRAIT ON PHONES (card-f5 item 3) ─────────────────────────────────────────────────────────
 *
 * The operator, 2026-10-13: «Lock the app to portrait on phones — turning the phone squashes the
 * tables. Telegram Desktop stays as it is.» Every landscape overlap report from prod that day was a
 * phone turned sideways (Android 919×413, 891×411, 825×360; iOS 852×393, 667×375).
 *
 * TELEGRAM'S LOCK FREEZES THE CURRENT ORIENTATION (Bot API 8.0 `lockOrientation`: «locks the Mini
 * App's orientation to its current mode (either portrait or landscape)»). So the app asks for it only
 * while the phone is upright; while a phone shows the app sideways — opened sideways, or a client
 * older than 8.0 that cannot lock — the «turn your phone upright» screen (`UprightScreen`) covers it,
 * and the lock is taken the moment the phone is upright again.
 *
 * ⚠️ THE ORIENTATION IS THE SCREEN'S, NEVER THE WINDOW'S SHAPE. An open keyboard makes an upright
 * phone's window wider than tall (the 414×191 iPhone report), and `forceReflow()` fires synthetic
 * `resize`s all day: a decision on `innerWidth > innerHeight` would cover a chat mid-sentence.
 * `screen.orientation.type` is the display's (Android) / the interface's (iOS) orientation;
 * `window.orientation` covers iOS below 16.4; neither → "unknown", and unknown changes nothing.
 *
 * Telegram Desktop and the web clients never reach any of this: no listener, no call, no screen.
 */

/** Telegram's phone clients (`tgWebAppPlatform`). Everything else is left exactly as it was. */
const PHONE_PLATFORMS: ReadonlySet<string> = new Set(["android", "android_x", "ios"]);

/**
 * A phone platform's device whose SCREEN's shorter side is at least this many CSS px is a TABLET,
 * and keeps today's free rotation. Telegram reports an iPad as `ios` and an Android tablet as
 * `android`, so the platform alone cannot tell. 600 is Android's own phone/tablet line (sw600dp);
 * every iPhone is ≤ 440 wide, every iPad ≥ 744.
 */
export const TABLET_SHORT_SIDE_PX = 600;

export type ScreenOrientationKind = "portrait" | "landscape" | "unknown";

/** Is this a Telegram PHONE client? An unreadable screen is not known to be one. Pure. */
export function isPhoneClient(platform: string | null, shortSide: number): boolean {
  if (platform === null || !PHONE_PLATFORMS.has(platform)) return false;
  if (!Number.isFinite(shortSide) || shortSide <= 0) return false;
  return shortSide < TABLET_SHORT_SIDE_PX;
}

/** The screen's shorter side in CSS px (iOS never swaps `screen.width/height`, Android does —
 *  only the min is portable), or NaN when it cannot be read. */
export function screenShortSide(): number {
  const { width, height } = window.screen;
  return width > 0 && height > 0 ? Math.min(width, height) : Number.NaN;
}

/** How the app is shown — see the file comment for why this never looks at the window. */
export function readScreenOrientation(): ScreenOrientationKind {
  const type: unknown = window.screen.orientation?.type;
  if (typeof type === "string") {
    if (type.startsWith("portrait")) return "portrait";
    if (type.startsWith("landscape")) return "landscape";
  }
  // iOS below 16.4 has no `screen.orientation`; its `window.orientation` is 0 / 180 upright,
  // 90 / -90 sideways.
  const angle: unknown = (window as unknown as { orientation?: unknown }).orientation;
  if (typeof angle === "number") return Math.abs(angle) === 90 ? "landscape" : "portrait";
  return "unknown";
}

export interface OrientationDecision {
  /** Hold Telegram's lock (true), give it back (false), or leave it as it is (null). */
  lock: boolean | null;
  /** Cover the app with the «turn your phone upright» screen. */
  upright: boolean;
}

/**
 * The whole decision, pure. A phone upright locks (when the client can); a phone sideways never
 * locks — and gives back a lock it holds, because a lock that landed mid-turn (or a client that let
 * it go) would otherwise hold the app sideways under a screen asking to turn it upright, a dead end;
 * a phone whose orientation cannot be read changes nothing; anything that is not a phone holds no
 * lock and never shows the screen.
 */
export function decideOrientation({
  phone,
  orientation,
  lockSupported,
}: {
  phone: boolean;
  orientation: ScreenOrientationKind;
  lockSupported: boolean;
}): OrientationDecision {
  if (!phone) return { lock: false, upright: false };
  if (orientation === "portrait") return { lock: lockSupported, upright: false };
  if (orientation === "landscape") return { lock: false, upright: true };
  return { lock: null, upright: false };
}

// ── the upright screen's state: a tiny external store, so a turn re-renders that screen alone ──
let uprightNeeded = false;
const uprightListeners = new Set<() => void>();

function setUprightNeeded(next: boolean): void {
  if (next === uprightNeeded) return;
  uprightNeeded = next;
  for (const listener of [...uprightListeners]) listener();
}

export function isUprightNeeded(): boolean {
  return uprightNeeded;
}

export function subscribeUpright(listener: () => void): () => void {
  uprightListeners.add(listener);
  return () => {
    uprightListeners.delete(listener);
  };
}

/** React: is the «turn your phone upright» screen needed right now? */
export function useUprightNeeded(): boolean {
  return useSyncExternalStore(subscribeUpright, isUprightNeeded, isUprightNeeded);
}

/** Post `web_app_toggle_orientation_lock` through the SDK. False when the client refused it. */
function postOrientationLock(locked: boolean): boolean {
  try {
    postEvent("web_app_toggle_orientation_lock", { locked });
    return true;
  } catch {
    return false;
  }
}

/**
 * Start keeping a phone's Mini App upright. Called ONCE from `bootstrap()` (main.tsx), right after
 * `setupViewport()` — whose `init()` has run by then — and never from a React effect (StrictMode
 * would mount it twice). Returns a stop function (tests).
 *
 * The lock is sent once per entry into portrait, and re-asserted when the page comes back to the
 * foreground upright: Telegram re-opens a cached WebView without reloading the page (viewport.ts's
 * self-heal), so this boot code does not run again, and a lock given back on close would be gone.
 */
export function startOrientationLock({
  platform,
  version,
}: {
  platform: string | null;
  version: string | null;
}): () => void {
  // BEFORE the SDK: its `postEvent` finds `TelegramWebviewProxy` through a valibot key check that
  // calls a bare `Object.hasOwn` (Chrome 93) — see `ensureSdkPolyfills`.
  ensureSdkPolyfills();
  // Telegram Desktop, the web clients, unknown, outside Telegram: exactly today's behaviour.
  if (platform === null || !PHONE_PLATFORMS.has(platform)) return () => undefined;

  // The bridge's own version table (8.0+). No version, no lock: the upright screen is the fallback.
  let lockSupported = version !== null && supports("web_app_toggle_orientation_lock", version);
  let locked = false;

  const evaluate = (reassert: boolean): void => {
    const decision = decideOrientation({
      phone: isPhoneClient(platform, screenShortSide()),
      orientation: readScreenOrientation(),
      lockSupported,
    });
    if (decision.lock !== null && (decision.lock !== locked || (reassert && decision.lock))) {
      if (postOrientationLock(decision.lock)) {
        locked = decision.lock;
      } else {
        // A client that refuses the event is not asked again; the upright screen still covers it.
        lockSupported = false;
        locked = false;
      }
    }
    setUprightNeeded(decision.upright);
  };

  const onTurn = (): void => evaluate(false);
  const onVisibility = (): void => {
    if (!document.hidden) evaluate(true);
  };
  const detach: Array<() => void> = [];
  const listen = (target: EventTarget | undefined, type: string, fn: () => void): void => {
    if (!target) return;
    target.addEventListener(type, fn);
    detach.push(() => target.removeEventListener(type, fn));
  };
  // `screen.orientation` is missing on iOS below 16.4 — `orientationchange` covers it there.
  listen(window.screen.orientation, "change", onTurn);
  listen(window, "orientationchange", onTurn);
  listen(window, "resize", onTurn);
  listen(document, "visibilitychange", onVisibility);

  evaluate(false);

  return () => {
    for (const off of detach) off();
    setUprightNeeded(false);
  };
}
